import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CreateIcon from "@mui/icons-material/Create";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MailIcon from "@mui/icons-material/Mail";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";

import { CancelShipmentBookingModal } from "components/CancelShipmentBookingModal";
import { MarkShipmentAsDeliveredModal } from "components/MarkShipmentAsDeliveredModal";
import { MarkShipmentAsPickedUpModal } from "components/MarkShipmentAsPickedUpModal";
import { useDisclosure, useShipperShipmentPageContext } from "hooks";
import { shipperHasWriteAccess } from "utils";

import { AddCustomersToShipment } from "./components/AddCustomersToShipment";
import { AddPartnersToShipment } from "./components/AddPartnersToShipment";
import { CancelOrFinalizeShipment } from "./components/CancelOrFinalizeShipment";
import { CancelShipmentBookingSideBarMenuOption } from "./components/CancelShipmentBookingSideBarMenuOption";
import { FinalizeProjectModal } from "./components/FinalizeProjectModal";
import { MarkBookedOffPlatformMenuOption } from "./components/MarkBookedOffPlatformMenuOption";
import { MarkShipmentAsPickedUpMenuOption } from "./components/MarkShimentAsPickedupMenuOption";
import { MarkShipmentAsDeliveredMenuOption } from "./components/MarkShipmentAsDeliveredMenuOption";

interface Props {
  open: boolean;
  handleDrawerClose: () => void;
  onShareShipmentWithTeammatesOpen: () => void;
  onShipmentCopyOpen: () => void;
  onOpenCancelShipment: () => void;
  onCreateTemplateFromShipmentOpen: () => void;
  onUpdateEmailNotificationSettingsOpen: () => void;
  onOpenPdfPreview: () => void;
  onOpenNotifiedCarriersModal: () => void;
  onOpenMarkBookedOffPlatform: VoidFunction;
}

export function SideBarMenu(props: Props) {
  const { shipment, onRefresh } = useShipperShipmentPageContext();
  const {
    open,
    handleDrawerClose,
    onShareShipmentWithTeammatesOpen,
    onShipmentCopyOpen,
    onOpenCancelShipment,
    onCreateTemplateFromShipmentOpen,
    onUpdateEmailNotificationSettingsOpen,
    onOpenPdfPreview,
    onOpenNotifiedCarriersModal,
    onOpenMarkBookedOffPlatform,
  } = props;

  const {
    isOpen: finalizeProjectModalOpen,
    onClose: onCloseFinalizeProjectModal,
    onOpen: onOpenFinalizeProjectModal,
  } = useDisclosure(false);

  const {
    isOpen: addPartnersToShipmentModalOpen,
    onClose: onCloseAddPartnersToShipmentModal,
    onOpen: onOpenAddPartnersToShipmentModal,
  } = useDisclosure(false);

  const {
    isOpen: addCustomersToShipmentModalOpen,
    onClose: onCloseAddCustomersToShipmentModal,
    onOpen: onOpenAddCustomersToShipmentModal,
  } = useDisclosure(false);

  const {
    isOpen: cancelShipmentBookingModalOpen,
    onClose: onCloseCancelShipmentBookingModal,
    onOpen: onOpenCancelShipmentBookingModal,
  } = useDisclosure(false);

  const {
    isOpen: markShipmentAsDeliveredModalOpen,
    onClose: onCloseMarkShipmentAsDeliveredModal,
    onOpen: onOpenMarkShipmentAsDeliveredModal,
  } = useDisclosure(false);

  const {
    isOpen: markShipmentAsPickedUpModalOpen,
    onClose: onCloseMarkShipmentAsPickedUpModal,
    onOpen: onOpenMarkAsPickedUp,
  } = useDisclosure(false);

  if (!shipment || shipment === undefined) {
    return null;
  }

  return (
    <>
      <Drawer variant="persistent" anchor="right" open={open}>
        <div>
          <IconButton onClick={handleDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
        </div>
        <List>
          {shipment &&
            (shipment.access_type === "OWNER" ||
              shipment.access_type === "ADMIN") && (
              <ListItem>
                <ListItemButton onClick={onShareShipmentWithTeammatesOpen}>
                  <ListItemIcon>
                    <PersonAddIcon />
                  </ListItemIcon>
                  <ListItemText primary="Share Shipment" />
                </ListItemButton>
              </ListItem>
            )}
          <ListItem>
            <ListItemButton onClick={onOpenNotifiedCarriersModal}>
              <ListItemIcon>
                <VisibilityIcon />
              </ListItemIcon>
              <ListItemText primary="View Notified Carriers" />
            </ListItemButton>
          </ListItem>
          {shipment?.access_type === "OWNER" &&
            shipment.network_notification_type === "PRIVATE_SHIPMENT" &&
            shipment.shipment_status === "ACTIVE" && (
              <ListItem>
                <ListItemButton onClick={onOpenAddPartnersToShipmentModal}>
                  <ListItemIcon>
                    <LocalShippingIcon />
                  </ListItemIcon>
                  <ListItemText primary="Add Carrier Partners" />
                </ListItemButton>
              </ListItem>
            )}
          {shipment &&
            shipperHasWriteAccess(shipment.access_type) &&
            shipment.info.type !== "MULTI_SHIPMENT_PROJECT_CHILD" &&
            shipment.info.type !== "MULTI_SHIPMENT_PROJECT" && (
              <ListItem>
                <ListItemButton onClick={onOpenAddCustomersToShipmentModal}>
                  <ListItemIcon>
                    <GroupAddIcon />
                  </ListItemIcon>
                  <ListItemText primary="Add Customers" />
                </ListItemButton>
              </ListItem>
            )}
          <ListItem>
            <ListItemButton onClick={onShipmentCopyOpen}>
              <ListItemIcon>
                <ContentCopyIcon />
              </ListItemIcon>
              <ListItemText primary="Copy Shipment" />
            </ListItemButton>
          </ListItem>
          {shipment && (
            <ListItem>
              <Tooltip
                title={
                  shipment.info.type === "MULTI_SHIPMENT_PROJECT"
                    ? "Export is only supported for single shipments"
                    : ""
                }
                placement="left"
              >
                <span>
                  <ListItemButton
                    onClick={onOpenPdfPreview}
                    disabled={shipment.info.type === "MULTI_SHIPMENT_PROJECT"}
                  >
                    <ListItemIcon>
                      <PictureAsPdfIcon />
                    </ListItemIcon>
                    <ListItemText primary="Export PDF" />
                  </ListItemButton>
                </span>
              </Tooltip>
            </ListItem>
          )}
          <CancelOrFinalizeShipment
            onOpenCancelShipment={onOpenCancelShipment}
            onFinalizeProjectClicked={onOpenFinalizeProjectModal}
          />
          <ListItem>
            <ListItemButton onClick={onCreateTemplateFromShipmentOpen}>
              <ListItemIcon>
                <CreateIcon />
              </ListItemIcon>
              <ListItemText primary="Create Template" />
            </ListItemButton>
          </ListItem>
          <MarkShipmentAsPickedUpMenuOption
            onOpenMarkAsPickedUp={onOpenMarkAsPickedUp}
          />
          <MarkShipmentAsDeliveredMenuOption
            onOpenMarkAsDelivered={onOpenMarkShipmentAsDeliveredModal}
          />
          <CancelShipmentBookingSideBarMenuOption
            onOpenCancelShipmentBooking={onOpenCancelShipmentBookingModal}
          />
          <MarkBookedOffPlatformMenuOption
            onOpenMarkBookedOffPlatform={onOpenMarkBookedOffPlatform}
          />
          {shipment?.access_type === "OWNER" && (
            <ListItem>
              <ListItemButton onClick={onUpdateEmailNotificationSettingsOpen}>
                <ListItemIcon>
                  <MailIcon />
                </ListItemIcon>
                <ListItemText primary="Update Notification Settings" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>
      <FinalizeProjectModal
        onModalClose={onCloseFinalizeProjectModal}
        modalOpen={finalizeProjectModalOpen}
      />
      <CancelShipmentBookingModal
        onModalClose={onCloseCancelShipmentBookingModal}
        modalOpen={cancelShipmentBookingModalOpen}
        shipmentId={shipment.info.id}
        onBookingCancelledSuccess={onRefresh}
      />
      <MarkShipmentAsDeliveredModal
        onModalClose={onCloseMarkShipmentAsDeliveredModal}
        modalOpen={markShipmentAsDeliveredModalOpen}
        shipmentId={shipment.info.id}
        onMarkDeliveredSuccess={onRefresh}
        quotingUnitOfMeasure={shipment.info.quoting_unit_of_measure}
      />
      <MarkShipmentAsPickedUpModal
        onModalClose={onCloseMarkShipmentAsPickedUpModal}
        modalOpen={markShipmentAsPickedUpModalOpen}
        shipmentId={shipment.info.id}
        onMarkPickedUpSuccess={onRefresh}
      />
      {shipment?.access_type === "OWNER" &&
        shipment.network_notification_type === "PRIVATE_SHIPMENT" && (
          <AddPartnersToShipment
            onModalClose={onCloseAddPartnersToShipmentModal}
            modalOpen={addPartnersToShipmentModalOpen}
          />
        )}
      {shipment &&
        shipperHasWriteAccess(shipment.access_type) &&
        shipment.info.type !== "MULTI_SHIPMENT_PROJECT_CHILD" &&
        shipment.info.type !== "MULTI_SHIPMENT_PROJECT" && (
          <AddCustomersToShipment
            onModalClose={onCloseAddCustomersToShipmentModal}
            modalOpen={addCustomersToShipmentModalOpen}
          />
        )}
    </>
  );
}
